import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Power, Wallet, Trash2, RotateCcw, EyeOff, Eye, RefreshCw, Copy, Check } from "lucide-react";
import Modal, { Field, Input, Select, GoldButton, GhostButton, PageHeader, EmptyState, StatusBadge } from "@/components/Ui";
import { SortableTh, useTableControls } from "@/components/SortableTable";
import { fmtDate, fmtDateTime } from "@/lib/time";

const EMPTY_FORM = {
    name: "", slug: "", owner_name: "", owner_email: "", owner_phone: "",
    owner_password: "", plan_id: "",
    payment_method: "", payment_amount: "", payment_transaction_id: "", payment_remarks: "",
    is_trial: false, trial_days: "14",
    is_founding_member: false,
    onboarding_notes: "",
};

function generatePassword(len = 12) {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$";
    return Array.from({ length: len }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
}

function slugify(str) {
    return str.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

export default function AdminTenants() {
    const [items, setItems] = useState([]);
    const [plans, setPlans] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [showPw, setShowPw] = useState(false);
    const [pwCopied, setPwCopied] = useState(false);
    const [saving, setSaving] = useState(false);
    const [creditTenant, setCreditTenant] = useState(null);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [showDeleted, setShowDeleted] = useState(false);
    const slugManualRef = useRef(false);

    const load = useCallback(async () => {
        try {
            const [t, p] = await Promise.all([
                api.get("/tenants", { params: showDeleted ? { include_deleted: true } : {} }),
                api.get("/plans"),
            ]);
            setItems(t.data); setPlans(p.data);
            setSelectedIds(new Set());
        } catch (e) { toast.error(apiError(e)); }
    }, [showDeleted]);
    useEffect(() => { load(); }, [load]);

    // Auto-generate slug from firm name (unless user has manually edited slug)
    useEffect(() => {
        if (!slugManualRef.current && form.name) {
            setForm(f => ({ ...f, slug: slugify(f.name) }));
        }
    }, [form.name]);

    const genPassword = () => {
        const pw = generatePassword();
        setForm(f => ({ ...f, owner_password: pw }));
        setShowPw(true);
        setPwCopied(false);
    };

    const copyPassword = () => {
        navigator.clipboard.writeText(form.owner_password).then(() => {
            setPwCopied(true);
            setTimeout(() => setPwCopied(false), 2000);
        });
    };

    const submit = async (e) => {
        e.preventDefault(); setSaving(true);
        try {
            const payload = { ...form };
            // Numeric coercions
            if (payload.trial_days) payload.trial_days = parseInt(payload.trial_days, 10);
            // Strip empty payment fields
            if (!payload.payment_method) {
                delete payload.payment_method;
                delete payload.payment_amount;
                delete payload.payment_transaction_id;
                delete payload.payment_remarks;
            } else {
                if (payload.payment_amount === "") delete payload.payment_amount;
                else payload.payment_amount = parseFloat(payload.payment_amount);
            }
            // Strip empty optional strings
            if (!payload.owner_phone) delete payload.owner_phone;
            if (!payload.onboarding_notes) delete payload.onboarding_notes;
            await api.post("/tenants", payload);
            toast.success("Tenant onboarded" + (payload.payment_method ? ` · payment recorded (${payload.payment_method.toUpperCase()})` : ""));
            setOpen(false); load();
            setForm(EMPTY_FORM);
            slugManualRef.current = false;
            setShowPw(false);
        } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
    };

    const toggleStatus = async (t) => {
        const status = t.status === "active" ? "suspended" : "active";
        try { await api.patch(`/tenants/${t.id}`, { status }); load(); toast.success(`Tenant ${status}`); }
        catch (e) { toast.error(apiError(e)); }
    };

    const deleteSingle = async (t) => {
        if (!window.confirm(`Delete "${t.name}"? This soft-deletes the tenant and all its users. Auto-purged in 30 days. You can restore from "Show deleted".`)) return;
        try { await api.delete(`/tenants/${t.id}`); toast.success("Tenant moved to trash"); load(); }
        catch (e) { toast.error(apiError(e)); }
    };

    const restoreSingle = async (t) => {
        try { await api.post(`/tenants/${t.id}/restore`); toast.success("Tenant restored"); load(); }
        catch (e) { toast.error(apiError(e)); }
    };

    const purgeSingle = async (t) => {
        const phrase = `DELETE ${t.name}`;
        const typed = window.prompt(
            `PERMANENT DELETE — this CANNOT be undone.\n\n` +
            `Tenant "${t.name}" and ALL its data across 31 collections (cases, hearings, invoices, payments, documents, chat, etc.) will be wiped from MongoDB.\n\n` +
            `To proceed, type exactly:  ${phrase}`,
        );
        if (typed !== phrase) {
            if (typed !== null) toast.error("Confirmation did not match — aborted");
            return;
        }
        try {
            const { data } = await api.delete(`/tenants/${t.id}/purge`);
            const removed = Object.values(data.summary || {}).reduce((a, b) => a + Number(b || 0), 0);
            toast.success(`Purged ${t.name} · ${removed} document(s) wiped`);
            load();
        } catch (e) { toast.error(apiError(e)); }
    };

    const toggleSelect = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const toggleAll = () => {
        const visible = items.filter(t => !t.deleted_at).map(t => t.id);
        if (selectedIds.size === visible.length) setSelectedIds(new Set());
        else setSelectedIds(new Set(visible));
    };

    const bulkDelete = async () => {
        const ids = Array.from(selectedIds);
        if (ids.length === 0) return;
        if (!window.confirm(`Delete ${ids.length} tenant(s)? Soft-delete with 30-day retention. Continue?`)) return;
        try {
            const { data } = await api.post("/tenants/bulk-delete", { ids });
            toast.success(`${data.deleted_count} tenant(s) moved to trash`);
            load();
        } catch (e) { toast.error(apiError(e)); }
    };

    const activeVisibleCount = items.filter(t => !t.deleted_at).length;
    const allActiveSelected = activeVisibleCount > 0 && selectedIds.size === activeVisibleCount;

    const { sort, setSort, rows: sortedItems, ...ctrlExtra } = useTableControls(items, { initialSort: { key: "created_at", dir: "desc" } });
    const tableCtrl = { sort, setSort, rows: sortedItems, ...ctrlExtra };

    return (
        <>
            <PageHeader
                title="Tenants"
                subtitle="Every firm on the platform. Activate, suspend, audit."
                action={<GoldButton onClick={() => setOpen(true)} data-testid="tenants-add-btn"><Plus className="w-4 h-4 inline mr-2" strokeWidth={2}/>Onboard tenant</GoldButton>}
            />

            <div className="flex items-center gap-3 mb-3 text-xs flex-wrap">
                <label className="inline-flex items-center gap-2 cursor-pointer text-muted-foreground hover:text-foreground" data-testid="tenants-toggle-trash">
                    <input type="checkbox" checked={showDeleted} onChange={e => setShowDeleted(e.target.checked)}/>
                    <EyeOff className="w-3 h-3" strokeWidth={1.5}/>
                    Show deleted (trash)
                </label>
                {selectedIds.size > 0 && (
                    <div className="ml-auto inline-flex items-center gap-2 border border-red-700/50 bg-red-950/30 px-3 py-1.5">
                        <span className="text-red-300 text-xs">{selectedIds.size} selected</span>
                        <button onClick={bulkDelete} data-testid="tenants-bulk-delete-btn"
                            className="text-xs text-red-300 hover:text-red-100 inline-flex items-center gap-1">
                            <Trash2 className="w-3 h-3" strokeWidth={1.5}/>Bulk delete
                        </button>
                        <button onClick={() => setSelectedIds(new Set())} className="text-xs text-muted-foreground hover:text-foreground">Clear</button>
                    </div>
                )}
            </div>

            {items.length === 0 ? (
                <EmptyState title="No tenants yet" action={<GoldButton onClick={() => setOpen(true)} data-testid="tenants-empty-add">Onboard</GoldButton>}/>
            ) : (
                <div className="border border-border overflow-x-auto">
                    <table className="min-w-[720px] w-full text-sm">
                        <thead className="bg-muted border-b border-border">
                            <tr className="text-left">
                                <th className="px-3 py-3 w-10">
                                    <input type="checkbox" checked={allActiveSelected} onChange={toggleAll} data-testid="tenants-select-all"/>
                                </th>
                                <SortableTh ctrl={tableCtrl} field="name" testIdPrefix="sort-tenants">Firm</SortableTh>
                                <SortableTh ctrl={tableCtrl} field="slug" testIdPrefix="sort-tenants">Slug</SortableTh>
                                <SortableTh ctrl={tableCtrl} field="status" testIdPrefix="sort-tenants">Status</SortableTh>
                                <SortableTh ctrl={tableCtrl} field="users_count" testIdPrefix="sort-tenants">Users</SortableTh>
                                <SortableTh ctrl={tableCtrl} field="cases_count" testIdPrefix="sort-tenants">Litigations</SortableTh>
                                <SortableTh ctrl={tableCtrl} field="created_at" testIdPrefix="sort-tenants">Joined</SortableTh>
                                <th className="px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedItems.map(t => {
                                const isDeleted = !!t.deleted_at;
                                return (
                                <tr key={t.id} className={`border-b border-border hover:bg-muted/50 ${isDeleted ? "opacity-50" : ""}`} data-testid={`tenant-row-${t.id}`}>
                                    <td className="px-3 py-3">
                                        {!isDeleted && (
                                            <input type="checkbox" checked={selectedIds.has(t.id)} onChange={() => toggleSelect(t.id)} data-testid={`tenant-select-${t.id}`}/>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        {isDeleted ? (
                                            <span>{t.name}<span className="ml-2 text-[10px] text-red-400 uppercase">deleted</span></span>
                                        ) : (
                                            <Link to={`/admin/tenants/${t.id}`} data-testid={`tenant-open-${t.id}`}
                                                className="text-foreground hover:text-primary hover:underline font-medium">
                                                {t.name}
                                            </Link>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{t.slug}</td>
                                    <td className="px-4 py-3"><StatusBadge status={t.status}/></td>
                                    <td className="px-4 py-3 text-muted-foreground">{t.users_count}</td>
                                    <td className="px-4 py-3 text-muted-foreground">{t.cases_count}</td>
                                    <td className="px-4 py-3 text-xs text-muted-foreground">{fmtDate(t.created_at)}</td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="inline-flex gap-3 items-center">
                                            {isDeleted ? (
                                                <>
                                                    <button onClick={() => restoreSingle(t)} data-testid={`tenant-restore-${t.id}`}
                                                        className="text-xs text-emerald-400 hover:underline inline-flex items-center gap-1">
                                                        <RotateCcw className="w-3 h-3" strokeWidth={1.5}/>Restore
                                                    </button>
                                                    <button onClick={() => purgeSingle(t)} data-testid={`tenant-purge-${t.id}`}
                                                        className="text-xs text-red-500 hover:underline inline-flex items-center gap-1">
                                                        <Trash2 className="w-3 h-3" strokeWidth={1.5}/>Purge forever
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    <button onClick={() => setCreditTenant({ id: t.id, name: t.name })} data-testid={`tenant-credit-${t.id}`}
                                                        className="text-xs text-emerald-400 hover:underline inline-flex items-center gap-1">
                                                        <Wallet className="w-3 h-3" strokeWidth={1.5}/>Credit wallet
                                                    </button>
                                                    <button onClick={() => toggleStatus(t)} data-testid={`tenant-toggle-${t.id}`}
                                                        className="text-xs text-primary hover:underline inline-flex items-center gap-1"><Power className="w-3 h-3" strokeWidth={1.5}/>{t.status === "active" ? "Suspend" : "Activate"}</button>
                                                    <button onClick={() => deleteSingle(t)} data-testid={`tenant-delete-${t.id}`}
                                                        className="text-xs text-red-400 hover:underline inline-flex items-center gap-1">
                                                        <Trash2 className="w-3 h-3" strokeWidth={1.5}/>Delete
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );})}
                        </tbody>
                    </table>
                </div>
            )}

            <Modal open={open} onClose={() => { setOpen(false); setForm(EMPTY_FORM); slugManualRef.current = false; setShowPw(false); }} title="Onboard new tenant" size="lg" testId="tenant-modal">
                <form onSubmit={submit} className="space-y-4">
                    {/* ---- Core firm + owner details ---- */}
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Firm name">
                            <Input required value={form.name}
                                onChange={e => setForm({ ...form, name: e.target.value })}
                                data-testid="tenant-name-input"/>
                        </Field>
                        <Field label="Slug (URL key)" hint="Auto-generated from firm name — editable.">
                            <Input required value={form.slug}
                                onChange={e => { slugManualRef.current = true; setForm({ ...form, slug: e.target.value.toLowerCase().replace(/\s+/g, "-") }); }}
                                data-testid="tenant-slug-input"/>
                        </Field>
                        <Field label="Owner name">
                            <Input required value={form.owner_name} onChange={e => setForm({ ...form, owner_name: e.target.value })} data-testid="tenant-owner-name"/>
                        </Field>
                        <Field label="Owner email">
                            <Input required type="email" value={form.owner_email} onChange={e => setForm({ ...form, owner_email: e.target.value })} data-testid="tenant-owner-email"/>
                        </Field>
                        <Field label="Owner mobile / WhatsApp" hint="Used for welcome WA message.">
                            <Input type="tel" value={form.owner_phone} onChange={e => setForm({ ...form, owner_phone: e.target.value })} placeholder="+91 9XXXXXXXXX" data-testid="tenant-owner-phone"/>
                        </Field>
                        <Field label="Owner password">
                            <div className="flex gap-1 items-center">
                                <div className="relative flex-1">
                                    <Input required minLength="6" type={showPw ? "text" : "password"}
                                        value={form.owner_password}
                                        onChange={e => setForm({ ...form, owner_password: e.target.value })}
                                        data-testid="tenant-owner-password"
                                        className="pr-8"/>
                                    <button type="button" onClick={() => setShowPw(v => !v)}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                        data-testid="tenant-pw-toggle">
                                        {showPw ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
                                    </button>
                                </div>
                                <button type="button" onClick={genPassword} title="Auto-generate password"
                                    className="border border-border px-2 py-1.5 text-xs hover:bg-muted flex items-center gap-1"
                                    data-testid="tenant-pw-generate">
                                    <RefreshCw className="w-3.5 h-3.5"/>Gen
                                </button>
                                <button type="button" onClick={copyPassword} title="Copy password"
                                    disabled={!form.owner_password}
                                    className="border border-border px-2 py-1.5 text-xs hover:bg-muted"
                                    data-testid="tenant-pw-copy">
                                    {pwCopied ? <Check className="w-3.5 h-3.5 text-emerald-400"/> : <Copy className="w-3.5 h-3.5"/>}
                                </button>
                            </div>
                        </Field>
                        <Field label="Plan">
                            <Select value={form.plan_id} onChange={e => setForm({ ...form, plan_id: e.target.value })}
                                options={[{ value: "", label: "— select —" }, ...plans.map(p => ({ value: p.id, label: `${p.name} · ${p.price == null ? "Custom" : `₹${Number(p.price).toLocaleString("en-IN")}`}` }))]}
                                data-testid="tenant-plan-select"/>
                        </Field>
                    </div>

                    {/* ---- Trial / Founding Member flags ---- */}
                    <div className="border border-border p-4 space-y-3">
                        <div className="overline">Onboarding options</div>
                        <div className="flex flex-wrap gap-6">
                            <label className="inline-flex items-center gap-2 cursor-pointer text-sm" data-testid="tenant-trial-toggle">
                                <input type="checkbox" checked={form.is_trial}
                                    onChange={e => setForm({ ...form, is_trial: e.target.checked })}
                                    className="accent-primary"/>
                                Start on trial
                            </label>
                            <label className="inline-flex items-center gap-2 cursor-pointer text-sm" data-testid="tenant-founding-toggle">
                                <input type="checkbox" checked={form.is_founding_member}
                                    onChange={e => setForm({ ...form, is_founding_member: e.target.checked })}
                                    className="accent-primary"/>
                                Founding Member <span className="text-[10px] text-muted-foreground">(increments seats sold)</span>
                            </label>
                        </div>
                        {form.is_trial && (
                            <Field label="Trial duration (days)">
                                <Input type="number" min="1" max="365" value={form.trial_days}
                                    onChange={e => setForm({ ...form, trial_days: e.target.value })}
                                    className="w-32" data-testid="tenant-trial-days"/>
                            </Field>
                        )}
                    </div>

                    {/* ---- Onboarding notes ---- */}
                    <Field label="Onboarding notes (internal)" hint="Not visible to the tenant.">
                        <textarea value={form.onboarding_notes}
                            onChange={e => setForm({ ...form, onboarding_notes: e.target.value })}
                            rows={2}
                            placeholder="e.g. Referred by partner X · paid via offline bank transfer"
                            className="w-full bg-background border border-border px-3 py-2 text-sm resize-y focus:outline-none focus:ring-1 focus:ring-primary"
                            data-testid="tenant-onboarding-notes"/>
                    </Field>

                    {/* ---- Optional off-platform subscription payment (cash / UPI / NEFT) ---- */}
                    {form.plan_id && (
                        <div className="border border-border p-4 space-y-3">
                            <div>
                                <div className="overline">Subscription payment (optional)</div>
                                <div className="text-[11px] text-muted-foreground mt-1">
                                    If the firm paid off-platform (failed Razorpay etc.), capture it here. The subscription is activated regardless — these fields create the audit trail.
                                </div>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <Field label="Payment method">
                                    <Select value={form.payment_method}
                                        onChange={e => setForm({ ...form, payment_method: e.target.value })}
                                        options={[{ value: "", label: "— none / Razorpay later —" }, ...PAYMENT_METHODS]}
                                        data-testid="tenant-payment-method"/>
                                </Field>
                                <Field label="Amount (₹)" hint="Leave blank to default to plan price.">
                                    <Input type="number" min="0" step="0.01"
                                        value={form.payment_amount}
                                        onChange={e => setForm({ ...form, payment_amount: e.target.value })}
                                        disabled={!form.payment_method}
                                        data-testid="tenant-payment-amount"/>
                                </Field>
                                <Field label="Transaction / Reference ID" hint="UTR · cheque no · bank ref — required for non-cash.">
                                    <Input value={form.payment_transaction_id}
                                        onChange={e => setForm({ ...form, payment_transaction_id: e.target.value })}
                                        disabled={!form.payment_method}
                                        data-testid="tenant-payment-txnid"/>
                                </Field>
                                <Field label="Remarks (optional)">
                                    <Input value={form.payment_remarks}
                                        onChange={e => setForm({ ...form, payment_remarks: e.target.value })}
                                        disabled={!form.payment_method}
                                        placeholder="e.g. Razorpay failure · paid via PhonePe UPI"
                                        data-testid="tenant-payment-remarks"/>
                                </Field>
                            </div>
                        </div>
                    )}

                    <div className="flex justify-end pt-2">
                        <GoldButton disabled={saving} data-testid="tenant-save-btn">{saving ? "Creating…" : "Onboard"}</GoldButton>
                    </div>
                </form>
            </Modal>

            {creditTenant && (
                <CreditWalletModal
                    tenant={creditTenant}
                    onClose={(refresh) => { setCreditTenant(null); if (refresh) load(); }}
                />
            )}
        </>
    );
}

// ============================================================================
// Manual wallet credit (off-platform payments: cash / UPI / NEFT / cheque ...)
// ============================================================================
const PAYMENT_METHODS = [
    { value: "upi",            label: "UPI" },
    { value: "neft",           label: "NEFT" },
    { value: "imps",           label: "IMPS" },
    { value: "rtgs",           label: "RTGS" },
    { value: "bank_transfer",  label: "Bank transfer" },
    { value: "cheque",         label: "Cheque" },
    { value: "cash",           label: "Cash" },
];

function CreditWalletModal({ tenant, onClose }) {
    const [info, setInfo] = useState(null);
    const [form, setForm] = useState({
        amount: "", payment_method: "upi", transaction_id: "", remarks: "",
    });
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let alive = true;
        api.get(`/admin/tenants/${tenant.id}/wallet`)
            .then(r => { if (alive) setInfo(r.data); })
            .catch(e => toast.error(apiError(e)));
        return () => { alive = false; };
    }, [tenant.id]);

    const submit = async (e) => {
        e.preventDefault();
        const amt = parseFloat(form.amount);
        if (!amt || amt <= 0) { toast.error("Amount must be positive"); return; }
        if (!form.transaction_id.trim()) { toast.error("Transaction / reference ID is required for audit"); return; }
        setBusy(true);
        try {
            const { data } = await api.post(`/admin/tenants/${tenant.id}/wallet/credit`, {
                amount: amt,
                payment_method: form.payment_method,
                transaction_id: form.transaction_id.trim(),
                remarks: form.remarks?.trim() || null,
            });
            if (data.duplicate) {
                toast.warning("Already credited", { description: data.message });
            } else {
                toast.success(`₹${amt.toLocaleString()} credited to ${tenant.name}. New balance: ₹${data.balance.toFixed(2)}`);
            }
            onClose(true);
        } catch (err) { toast.error(apiError(err)); }
        finally { setBusy(false); }
    };

    return (
        <Modal open={true} onClose={() => onClose(false)}
            title={`Credit wallet · ${tenant.name}`} size="lg" testId="credit-wallet-modal">
            {info && (
                <div className="bg-background border border-border px-4 py-3 mb-4 flex items-baseline gap-4">
                    <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Current balance</span>
                    <span className="text-2xl font-light text-primary" data-testid="credit-current-balance">
                        ₹{Number(info.balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </span>
                    <span className="text-[10px] text-muted-foreground ml-auto">{info.currency || "INR"}</span>
                </div>
            )}

            <form onSubmit={submit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Amount (₹)" hint="The amount the tenant paid you off-platform.">
                        <Input required type="number" min="1" step="0.01" value={form.amount}
                            onChange={e => setForm({ ...form, amount: e.target.value })}
                            data-testid="credit-amount-input"/>
                    </Field>
                    <Field label="Payment method">
                        <Select value={form.payment_method}
                            onChange={e => setForm({ ...form, payment_method: e.target.value })}
                            options={PAYMENT_METHODS}
                            data-testid="credit-method-select"/>
                    </Field>
                    <Field label="Transaction / Reference ID"
                        hint="Bank ref no · UPI UTR · cheque no — required for audit.">
                        <Input required value={form.transaction_id}
                            onChange={e => setForm({ ...form, transaction_id: e.target.value })}
                            data-testid="credit-txnid-input"/>
                    </Field>
                    <Field label="Remarks (optional)">
                        <Input value={form.remarks}
                            onChange={e => setForm({ ...form, remarks: e.target.value })}
                            placeholder="e.g. Failed Razorpay top-up · paid via UPI"
                            data-testid="credit-remarks-input"/>
                    </Field>
                </div>
                <div className="text-[11px] text-muted-foreground bg-muted border-l-2 border-primary p-3">
                    This action is <b className="text-primary">audit-logged</b> against your super-admin account.
                    The tenant will see this as a normal credit row in their wallet passbook.
                    Re-submitting the same transaction ID will be detected and the wallet won&apos;t be double-credited.
                </div>

                {info?.manual_credits?.length > 0 && (
                    <div className="border border-border p-3">
                        <div className="overline mb-2">Recent manual credits</div>
                        <table className="w-full text-[11px]">
                            <thead className="text-muted-foreground">
                                <tr><th className="text-left py-1">When</th><th className="text-left">Method</th><th className="text-left">Txn ID</th><th className="text-right">Amount</th></tr>
                            </thead>
                            <tbody>
                                {info.manual_credits.map(c => (
                                    <tr key={c.id} className="border-t border-border" data-testid={`recent-credit-${c.id}`}>
                                        <td className="py-1 text-muted-foreground">{fmtDateTime(c.created_at)}</td>
                                        <td className="uppercase text-muted-foreground">{c.payment_method}</td>
                                        <td className="font-mono text-[10px] text-muted-foreground">{c.external_txn_id}</td>
                                        <td className="text-right text-emerald-300">₹{Number(c.amount).toFixed(2)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="flex justify-end gap-2 pt-2">
                    <GhostButton type="button" onClick={() => onClose(false)} data-testid="credit-cancel-btn">Cancel</GhostButton>
                    <GoldButton disabled={busy} data-testid="credit-submit-btn">
                        {busy ? "Crediting…" : "Credit wallet"}
                    </GoldButton>
                </div>
            </form>
        </Modal>
    );
}